import { ErrorMessage } from '@hookform/error-message';
import cx from 'classnames';
import { useCallback, useEffect, useRef } from 'react';
import type { UseFormReturn, FieldPath, FieldErrors, FieldValues } from 'react-hook-form';

import { useStartTyping } from '@/hooks/useStartTyping';
import { FieldErrorBox } from '@/site/components/FieldErrorBox';
import { Input } from '@/site/components/Input';
import type { ValidationOptions } from '@/utils/validationRules';

import { b, e } from './style.css';

function ErrorDisplay(props: { children?: React.ReactNode }) {
    return (
        <FieldErrorBox
            className={e.error}
            {...props}
        />
    );
}

export function Field<T extends FieldValues>(props: Field.Props<T>) {
    const {
        autoFocus,
        defaultValue,
        disabled,
        readOnly,
        errors,
        name,
        onBlur,
        onChange,
        placeholder,
        register,
        type = 'text',
        size = 'large',
        validationRules = {},
        className: propsClassName,
        onStartTyping,
    } = props;

    const inputRef = useRef<HTMLInputElement | null>(null);

    const { handleChange: handleTypingChange, handleBlur: handleTypingBlur } = useStartTyping({ onStartTyping });

    const handleChange = useCallback(
        (ev: React.ChangeEvent<HTMLInputElement>) => {
            handleTypingChange(ev);
            onChange?.(ev);
        },
        [handleTypingChange, onChange],
    );

    const handleBlur = useCallback(
        (ev: React.FocusEvent<HTMLInputElement>) => {
            handleTypingBlur();
            onBlur?.(ev);
        },
        [handleTypingBlur, onBlur],
    );

    const { ref: registerRef, ...field } = register(name, {
        disabled,
        onBlur: handleBlur,
        onChange: handleChange,
        ...validationRules[name],
    });

    const setRef = useCallback(
        (el: HTMLInputElement | null) => {
            registerRef(el);
            inputRef.current = el;
        },
        [registerRef],
    );

    useEffect(() => {
        if (props.autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [props.autoFocus]);

    return (
        <div className={cx(b.name, propsClassName)}>
            <Input
                type={type}
                inputSize={size}
                defaultValue={defaultValue}
                disabled={disabled}
                readOnly={readOnly}
                placeholder={placeholder}
                state={errors[name] ? 'error' : 'default'}
                autoFocus={autoFocus}
                title={placeholder}
                ref={setRef}
                {...field}
            />
            <ErrorMessage
                errors={errors}
                name={name}
                as={ErrorDisplay}
            />
        </div>
    );
}

export namespace Field {
    type InputProps = React.ComponentPropsWithoutRef<typeof Input>;

    type HTMLInputFields =
        | 'onChange'
        | 'onBlur'
        | 'defaultValue'
        | 'autoFocus'
        | 'type'
        | 'placeholder'
        | 'disabled'
        | 'readOnly';

    export interface Props<T extends FieldValues>
        extends Pick<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputFields> {
        name: FieldPath<T>;
        errors: FieldErrors;
        register: UseFormReturn<T>['register'];
        size?: InputProps['inputSize'];
        validationRules?: Record<string, ValidationOptions>;
        triggerValidation?: UseFormReturn<T>['trigger'];
        className?: string;
        onStartTyping?: () => void;
    }
}
